import type { ParsedUrlQuery } from 'querystring';
import { Base64 } from 'js-base64';

import { logger } from '@packages/utilities';
import type { SearchUserInteraction } from '@packages/gql/generated/shopping/graphql';
import type { SearchApiSSuggestParameter, SelectedFilter } from './api/rest/types';
import type {
  FilterRangeValue,
  SelectedFilterValue,
  DesktopDisplayMode,
  PagingMode,
} from './types';

export type CompressedSelectedFilterValues = {
  [name: string]: string[];
};

/**
 * Get the currently selected filter values from the url query
 *
 * @param query the current url query, from router or next context
 * @returns Selected filter values in a compressed object format
 */
export const decodeSelectedFilterValues = (
  query: ParsedUrlQuery,
): CompressedSelectedFilterValues => {
  try {
    return typeof query.f === 'string'
      ? JSON.parse(Base64.decode(decodeURIComponent(query.f)))
      : {};
  } catch (error) {
    logger.debug({ error }, 'cannot decode filter query');
  }
  return {};
};

// this regex allows for a number to have optional decimals
// ("25-4500" and "25.5-4500.5" are allowed)
const rangeRegex = /^\d+\.?\d*-\d+\.?\d*$/;
const isRange = (compressedValue: string): boolean => rangeRegex.test(compressedValue);
const parseRange = (compressedValue: string): FilterRangeValue['range'] => {
  const [min, max] = compressedValue.split('-');

  return [parseFloat(min), parseFloat(max)];
};

/**
 * Convert selected filter values from a compressed object representation to a more detailed and useful form
 *
 * @param values Selected filter values in compressed object format
 * @param filters All currently available filters, from the API response
 * @returns selected filter values in a useful format, with proper labels and parsed numeric ranges
 */
export const decompressSelectedFilterValues = (
  values: CompressedSelectedFilterValues,
  filters: SelectedFilter[],
): SelectedFilterValue[] =>
  Object.entries(values)
    .map(([filterId, compressedValues]) => {
      const filter = filters.find((x) => x.id === filterId);

      if (!filter) return null;

      const range = compressedValues.find(isRange);
      const unit = 'slider' in filter ? filter.slider.rangeLabel : undefined;

      return {
        filterId,
        selectedValues: compressedValues.filter((value) => !isRange(value)),
        ...(range && unit ? { selectedRange: { unit, range: parseRange(range) } } : {}),
      };
    })
    .filter((x) => x !== null) as SelectedFilterValue[];

// TODO unit?
const serializeRange = (value: FilterRangeValue) =>
  `${value.range[0] ?? ''}-${value.range[1] ?? ''}`;

const compressSelectedFilterValue = (value: SelectedFilterValue) =>
  value.selectedValues.concat(value.selectedRange ? serializeRange(value.selectedRange) : []);

/**
 * Convert selected filter values from a detailed and useful representation into a compressed form suitable for persistence in the url query
 *
 * @param values Selected filter values in expanded representation
 * @returns selected filter values in a compressed object form, ids only
 */
export const compressSelectedFilterValues = (
  values: SelectedFilterValue[],
): CompressedSelectedFilterValues =>
  Object.fromEntries(
    values
      .filter((value) => value.selectedRange || value.selectedValues.length)
      .map((value) => [value.filterId, compressSelectedFilterValue(value)]),
  );

/**
 * Encode the currently selected filter values to base64, into the correct url query parameter
 *
 * @param values Selected filter values in compressed representation
 * @returns Partial url query, to be merged into the full query
 */
export const encodeSelectedFilterValues = (values: CompressedSelectedFilterValues) => {
  const stringified = JSON.stringify(values);

  return stringified === '{}' ? { f: undefined } : { f: Base64.encodeURI(stringified) };
};

export const extractSelectedFilterValues = (query: ParsedUrlQuery): ParsedUrlQuery =>
  typeof query.f === 'string' ? { f: query.f } : {};

/**
 * Extract the current page number from the url query
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Parsed page number, 1-based
 */

export const decodePage = (query: ParsedUrlQuery) => {
  // check if page number is a number string, else undefined to go to first page
  const currentPage =
    typeof query.p === 'string' && !Number.isNaN(parseInt(query.p, 10))
      ? parseInt(query.p, 10)
      : undefined;

  const pagingMode: PagingMode = currentPage === 0 ? 'infinite' : 'limited';

  return { pageSize: 72, currentPage, pagingMode };
};

/**
 * Encode a desired page number into the url query
 *
 * @param page Desired page number, 1-based
 * @returns Partial url query, to be merged into the full query
 */
export const encodePage = (page?: number) => ({
  p: page !== undefined ? page.toString(10) : page,
});

/**
 * Extract the current sorting order key
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Sorting key
 */
export const decodeOrder = (query: ParsedUrlQuery) =>
  typeof query.o === 'string' ? query.o : undefined;

/**
 * Encode a desired sorting order key into the url query
 *
 * @param order Sorting order key
 * @returns Partial url query, to be merged into the full query
 */
export const encodeOrder = (order: string | undefined) => ({ o: order });

export const extractOrder = (query: ParsedUrlQuery): ParsedUrlQuery =>
  typeof query.o === 'string' ? { o: query.o } : {};

/**
 * Extract the category id to be used as a special category filter for search result pages, separate from the other filters
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Category id (numeric id)
 */
export const decodeCategoryFilter = (query: ParsedUrlQuery) =>
  typeof query.c === 'string' ? query.c : undefined;

/**
 * Encode a category id as a special category filter used for search result pages, separate from the other filters
 *
 * @param categoryId Category id (numeric id)
 * @returns Partial url query, to be merged into the full query
 */
export const encodeCategoryFilter = (categoryId?: string) => ({ c: categoryId });

/**
 * Extract the current display mode value
 *
 * @param query Current url query, from router or Next SSR context
 * @returns display type value
 */
export const decodeDisplayMode = (query: ParsedUrlQuery): DesktopDisplayMode | undefined =>
  query.d === 'cards' || query.d === 'largeCards' || query.d === 'moreInformation'
    ? query.d
    : undefined;

/**
 * Encode a desired display mode value key into the url query
 *
 * @param displayValue display mode value
 * @returns Partial url query, to be merged into the full query
 */
export const encodeDisplayMode = (displayMode: DesktopDisplayMode | undefined) => ({
  d: displayMode,
});

export const extractDisplayMode = (query: ParsedUrlQuery): ParsedUrlQuery =>
  typeof query.d === 'string' ? { d: query.d } : {};

// NOTE: Consider Internal change to `q` instead of `searchquery` for consistency with other parameters
/**
 * Extract the current sorting order key
 *
 * @param query Current url query, from router or Next SSR context
 * @returns Sorting key
 */
export const decodeSearchQuery = (query: ParsedUrlQuery) =>
  typeof query.searchquery === 'string' ? query.searchquery.replace(/\+/g, ' ') : undefined;

/**
 * Encode a new search query into the url query
 *
 * @param searchquery New search query
 * @returns Partial url query, to be merged into the full query
 */
export const encodeSearchQuery = (searchquery: string) => ({ searchquery });

/**
 * Extract all parameters common to SERP and MBA from the url query
 *
 * @param query Current url query, from router or Next SSR context
 * @returns All shared parameters that are persisted in the url query
 */
export const decodeSharedQueryParamsForRestApi = (query: ParsedUrlQuery) => ({
  highlight: typeof query.highlight === 'string' ? query.highlight : undefined,
  order: decodeOrder(query),
  filters: decodeSelectedFilterValues(query),
  count: decodePage(query).pageSize,
  pageNoDisplay: decodePage(query).currentPage || 1,
  ssuggest: query.ssuggest as SearchApiSSuggestParameter,
  searchHistory: query.searchHistory === 'true' ? true : undefined,
});

/**
 * Encode a user interaction that leads to a search page into the url query
 */
export const encodeUserInteraction = (userInteraction: SearchUserInteraction | undefined) => {
  if (userInteraction === 'SEARCH_HISTORY') return { searchHistory: 'true', ssuggest: undefined };
  if (userInteraction === 'SUGGESTED_QUERY') return { searchHistory: undefined, ssuggest: 'sugg' };
  if (userInteraction === 'SUGGESTED_CATEGORY')
    return { searchHistory: undefined, ssuggest: 'kat' };
  if (userInteraction === 'SUGGESTED_PRODUCT')
    return { searchHistory: undefined, ssuggest: 'prod' };
  return { searchHistory: undefined, ssuggest: undefined };
};

/**
 * Extract the current user interaction type that led to this page from the url query
 */
export const decodeUserInteraction = (query: ParsedUrlQuery): SearchUserInteraction | undefined => {
  if (query.searchHistory === 'true') return 'SEARCH_HISTORY';
  if (query.ssuggest === 'sugg') return 'SUGGESTED_QUERY';
  if (query.ssuggest === 'kat') return 'SUGGESTED_CATEGORY';
  if (query.ssuggest === 'prod') return 'SUGGESTED_PRODUCT';
  return undefined;
};

/**
 * Extract the current promotion banner that led to this page from the url query
 */
export const decodePromotionId = (query: ParsedUrlQuery): string | undefined =>
  typeof query.lmPromo === 'string' ? query.lmPromo : undefined;

/**
 * Remove all `undefined` values from a query object
 */
export const pruneQuery = (query: ParsedUrlQuery): ParsedUrlQuery =>
  Object.fromEntries(Object.entries(query).filter((kvp) => kvp[1] !== undefined));
